import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

interface Business {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  description: string | null;
  city: string | null;
  address: string | null;
  status: string;
  verified: boolean;
  created_at: string;
}

interface FreelancerProfile {
  id: string;
  display_name: string | null;
  headline: string | null;
  skills: string | null;
  location: string | null;
}

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  moderator: 'Modérateur',
  business: 'Entreprise',
  freelancer: 'Freelance',
  instructor: 'Formateur',
  user: 'Utilisateur',
};

const roleSubtitles: Record<string, string> = {
  business: 'Voici l\u2019état de votre espace entreprise.',
  freelancer: 'Gérez vos services et votre profil freelance.',
  instructor: 'Publiez et suivez vos formations.',
  user: 'Publiez des annonces et gérez votre espace personnel.',
  super_admin: 'Supervision de la plateforme.',
  admin: 'Gestion de la plateforme.',
  moderator: 'Modération du contenu.',
};

const statusLabels: Record<string, string> = {
  pending: 'En attente de validation',
  active: 'Actif',
  suspended: 'Suspendu',
};

function Dashboard() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<Business | null>(null);
  const [freelancer, setFreelancer] = useState<FreelancerProfile | null>(null);
  const [courseCount, setCourseCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const role = profile?.role ?? 'user';

        if (role === 'business') {
          const { data, error: err } = await supabase
            .from('businesses')
            .select('*')
            .eq('owner_id', user.id)
            .maybeSingle();
          if (err) throw err;
          if (mounted) setBusiness(data as Business | null);
        } else if (role === 'freelancer') {
          const { data, error: err } = await supabase
            .from('freelancer_profiles')
            .select('*')
            .eq('owner_id', user.id)
            .maybeSingle();
          if (err) throw err;
          if (mounted) setFreelancer(data as FreelancerProfile | null);
        } else if (role === 'instructor') {
          const { count, error: err } = await supabase
            .from('courses')
            .select('*', { count: 'exact', head: true })
            .eq('owner_id', user.id);
          if (err) throw err;
          if (mounted) setCourseCount(count ?? 0);
        }
      } catch (e) {
        if (mounted) setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user, profile]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const role = profile?.role ?? 'user';
  const roleLabel = roleLabels[role] ?? role;
  const subtitle = roleSubtitles[role] ?? 'Voici l\u2019état de votre espace.';

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">
            Bonjour, {profile?.full_name || 'utilisateur'}
          </h1>
          <p className="text-sm text-foreground-400 mt-1">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-400 text-xs font-semibold whitespace-nowrap">
            {roleLabel}
          </span>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            Se déconnecter
          </button>
        </div>
      </div>

      {error ? (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
          {error}
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile card */}
          <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
                  <i className="ri-user-line text-primary-400" />
                </span>
                Mon profil
              </h2>
              <Link
                to="/dashboard/profile"
                className="text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
              >
                Modifier
              </Link>
            </div>
            <div className="space-y-3 text-sm flex-1">
              <div className="flex justify-between gap-4">
                <span className="text-foreground-400">Nom complet</span>
                <span className="text-foreground-50 font-medium text-right">{profile?.full_name || '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-foreground-400">Email</span>
                <span className="text-foreground-50 font-medium text-right">{profile?.email || user?.email || '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-foreground-400">Téléphone</span>
                <span className="text-foreground-50 font-medium text-right">{profile?.phone || '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-foreground-400">Rôle</span>
                <span className="text-foreground-50 font-medium text-right">{roleLabel}</span>
              </div>
            </div>
          </div>

          {/* Business card (business role) */}
          {role === 'business' && (
            <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
                    <i className="ri-store-line text-accent-400" />
                  </span>
                  Mon entreprise
                </h2>
                {business && (
                  <Link
                    to="/dashboard/business"
                    className="text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                  >
                    Modifier
                  </Link>
                )}
              </div>

              {business ? (
                <div className="space-y-3 text-sm flex-1">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-heading font-semibold text-foreground-50">{business.name}</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                          business.status === 'active'
                            ? 'bg-accent-500/15 text-accent-400'
                            : business.status === 'suspended'
                              ? 'bg-red-500/15 text-red-400'
                              : 'bg-secondary-500/15 text-secondary-400'
                        }`}
                      >
                        {statusLabels[business.status] ?? business.status}
                      </span>
                      {business.verified && (
                        <span className="px-2.5 py-1 rounded-full bg-primary-500/15 text-primary-400 text-xs font-semibold whitespace-nowrap">
                          <i className="ri-verified-badge-fill mr-1" />Vérifiée
                        </span>
                      )}
                    </div>
                  </div>
                  {business.description && (
                    <p className="text-foreground-400 leading-relaxed">{business.description}</p>
                  )}
                  <div className="flex justify-between gap-4">
                    <span className="text-foreground-400">Ville</span>
                    <span className="text-foreground-50 font-medium text-right">{business.city || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-foreground-400">Adresse</span>
                    <span className="text-foreground-50 font-medium text-right">{business.address || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-foreground-400">Créée le</span>
                    <span className="text-foreground-50 font-medium text-right">
                      {new Date(business.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 flex-1">
                  <p className="text-sm text-foreground-400 mb-4">
                    Vous n&apos;avez pas encore d&apos;entreprise enregistrée.
                  </p>
                  <Link
                    to="/dashboard/business"
                    className="inline-block px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
                  >
                    Créer mon entreprise
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Freelancer card (freelancer role) */}
          {role === 'freelancer' && (
            <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
                    <i className="ri-user-star-line text-accent-400" />
                  </span>
                  Mon profil freelance
                </h2>
                <Link
                  to="/dashboard/freelancer"
                  className="text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                >
                  Modifier
                </Link>
              </div>

              {freelancer ? (
                <div className="space-y-3 text-sm flex-1">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-heading font-semibold text-foreground-50">
                      {freelancer.display_name || profile?.full_name || 'Freelance'}
                    </span>
                    {user && (
                      <Link
                        to={`/freelancers/${user.id}`}
                        className="text-primary-400 hover:text-primary-300 text-xs font-medium whitespace-nowrap"
                      >
                        Voir public
                      </Link>
                    )}
                  </div>
                  {freelancer.headline && (
                    <p className="text-foreground-400">{freelancer.headline}</p>
                  )}
                  {freelancer.skills && (
                    <div className="flex flex-wrap gap-2">
                      {freelancer.skills.split(',').filter(Boolean).map((skill) => (
                        <span
                          key={skill}
                          className="px-2 py-0.5 rounded-full bg-secondary-500/10 text-secondary-400 text-xs font-medium"
                        >
                          {skill.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <span className="text-foreground-400">Localisation</span>
                    <span className="text-foreground-50 font-medium text-right">{freelancer.location || '—'}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 flex-1">
                  <p className="text-sm text-foreground-400 mb-4">
                    Complétez votre profil pour apparaître aux clients.
                  </p>
                  <Link
                    to="/dashboard/freelancer"
                    className="inline-block px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
                  >
                    Compléter mon profil
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Instructor card (instructor role) */}
          {role === 'instructor' && (
            <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
                    <i className="ri-graduation-cap-line text-accent-400" />
                  </span>
                  Mes formations
                </h2>
                <Link
                  to="/dashboard/courses"
                  className="text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                >
                  Gérer
                </Link>
              </div>

              <div className="space-y-3 text-sm flex-1">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-primary-500/10">
                    <i className="ri-graduation-cap-line text-3xl text-primary-400" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-3xl text-foreground-50">{courseCount}</p>
                    <p className="text-foreground-400">formation{courseCount === 1 ? '' : 's'} publiée{courseCount === 1 ? '' : 's'}</p>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Link
                  to="/dashboard/courses/new"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  Publier une formation
                </Link>
              </div>
            </div>
          )}

          {/* Generic card (user role) */}
          {role === 'user' && (
            <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
                    <i className="ri-megaphone-line text-accent-400" />
                  </span>
                  Mon espace
                </h2>
              </div>
              <p className="text-sm text-foreground-400 flex-1 leading-relaxed">
                Publiez des annonces, des produits et des biens immobiliers pour toucher la communauté
                WorldApeiron.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/dashboard/listings/new"
                  className="px-4 py-2.5 text-sm font-semibold bg-primary-500 text-foreground-950 rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap"
                >
                  Publier une annonce
                </Link>
                <Link
                  to="/dashboard/apartments/new"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap"
                >
                  Publier un bien
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Actions rapides */}
        <div className="mt-6 p-6 rounded-xl bg-background-100 border border-background-200/70">
          <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4">Actions rapides</h2>
          <div className="flex flex-wrap gap-3">
            {role === 'business' && (
              <>
                <Link
                  to="/dashboard/listings/new"
                  className="px-4 py-2.5 text-sm font-semibold bg-primary-500 text-foreground-950 rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-megaphone-line" />
                  Publier une annonce
                </Link>
                <Link
                  to="/dashboard/apartments/new"
                  className="px-4 py-2.5 text-sm font-semibold bg-primary-500 text-foreground-950 rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-add-line" />
                  Publier un bien
                </Link>
                <Link
                  to="/dashboard/jobs/new"
                  className="px-4 py-2.5 text-sm font-semibold bg-secondary-500 text-foreground-950 rounded-lg hover:bg-secondary-400 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-briefcase-4-line" />
                  Publier une offre d&apos;emploi
                </Link>
                <Link
                  to="/dashboard/listings"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-file-list-3-line" />
                  Mes annonces
                </Link>
                <Link
                  to="/dashboard/apartments"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-home-4-line" />
                  Mes appartements
                </Link>
                <Link
                  to="/dashboard/jobs"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-file-list-3-line" />
                  Mes offres d&apos;emploi
                </Link>
                <Link
                  to="/dashboard/media"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-gallery-line" />
                  Mon contenu média
                </Link>
                <Link
                  to="/dashboard/business"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-store-2-line" />
                  Modifier l&apos;entreprise
                </Link>
              </>
            )}

            {role === 'freelancer' && (
              <>
                <Link
                  to="/dashboard/gigs/new"
                  className="px-4 py-2.5 text-sm font-semibold bg-accent-500 text-foreground-950 rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-service-line" />
                  Proposer un service
                </Link>
                <Link
                  to="/dashboard/gigs"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-file-list-3-line" />
                  Mes services
                </Link>
                <Link
                  to="/dashboard/gigs/orders"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-shopping-bag-line" />
                  Mes commandes
                </Link>
                <Link
                  to="/dashboard/freelancer"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-user-star-line" />
                  Compléter mon profil
                </Link>
              </>
            )}

            {role === 'instructor' && (
              <>
                <Link
                  to="/dashboard/courses/new"
                  className="px-4 py-2.5 text-sm font-semibold bg-primary-500 text-foreground-950 rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-graduation-cap-line" />
                  Publier une formation
                </Link>
                <Link
                  to="/dashboard/courses"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-file-list-3-line" />
                  Mes formations
                </Link>
              </>
            )}

            {role === 'user' && (
              <>
                <Link
                  to="/dashboard/listings/new"
                  className="px-4 py-2.5 text-sm font-semibold bg-primary-500 text-foreground-950 rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-megaphone-line" />
                  Publier une annonce
                </Link>
                <Link
                  to="/dashboard/listings"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-file-list-3-line" />
                  Mes annonces
                </Link>
                <Link
                  to="/dashboard/apartments/new"
                  className="px-4 py-2.5 text-sm font-semibold bg-primary-500 text-foreground-950 rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-add-line" />
                  Publier un bien
                </Link>
                <Link
                  to="/dashboard/apartments"
                  className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-home-4-line" />
                  Mes appartements
                </Link>
              </>
            )}

            {/* Commun à tous les rôles */}
            <Link
              to="/dashboard/media"
              className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-gallery-line" />
              Mon contenu média
            </Link>
            <Link
              to="/dashboard/forum/new"
              className="px-4 py-2.5 text-sm font-semibold bg-secondary-500 text-foreground-950 rounded-lg hover:bg-secondary-400 transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-discuss-line" />
              Lancer une discussion
            </Link>
            <Link
              to="/dashboard/forum"
              className="px-4 py-2.5 text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-file-list-3-line" />
              Mes discussions
            </Link>
          </div>
        </div>
        </>
      )}
    </DashboardLayout>
  );
}

export default Dashboard;